<template>
  <span class="inline-action-buttons">
    <img class="img" v-if="img" :src="img"/>
    <a :title="$t('edit')"
       v-if="hasRightToEditPlan"
       @click="editPlanTreatment"
       class="data-table-action">
    <EditSvg class="svg-stroke-light"/><span class="action-button-mob-title">{{ $t('edit') }}</span>
  </a>
    <a :title="$t('refresh_prices_and_materials')"
       v-if="!isComplexesView && canEditPlan && rights.includes('refresh_plan_treatment_prices_button')"
       @click="updatePlanTreatmentPrices"
       class="data-table-action">
    <RefreshSvg/><span class="action-button-mob-title">{{ $t('refresh_prices_and_materials') }}</span>
  </a>
    <a :title="$t('sign')"
       v-if="!planTreatment.signed_at && canSign"
       @click="sign"
       class="data-table-action">
    <SignSvg class="svg-light svg-size-xl"/><span class="action-button-mob-title">{{ $t('sign') }}</span>
  </a>
    <a class="data-table-action"
       v-if="!isComplexesView"
       :title="$t('print')">
      <b-dropdown dropleft variant="link" no-caret>
        <template #button-content>
          <PrintSvg/><span class="action-button-mob-title">{{ $t('print') }}</span>
        </template>
        <a class="dropdown-item pointer" @click="printPlan">
          {{ $t('standard') }}
        </a>
        <a class="dropdown-item pointer" @click="printPlanGrouped">
          {{ $t('grouped') }}
        </a>
        <a class="dropdown-item pointer" @click="printPlanGroupedByTeeth">
          {{ $t('grouped_by_teeth') }}
        </a>
        <a class="dropdown-item pointer" @click="printPlanGroupedByServices">
          {{ $t('grouped_by_services') }}
        </a>
        <a class="dropdown-item pointer" @click="printPlanFact">
          {{ $t('plan_fact') }}
        </a>
         <a class="dropdown-item pointer" @click="printRouteList">
          {{ $t('route_list') }}
        </a>
        <div class="dropdown-divider"></div>
        <div class="dropdown-header font-weight-bold">{{  $t('with_formula') }}</div>
        <a class="dropdown-item pointer" @click="printWithFormula">
          {{ $t('standard') }}
        </a>
        <a class="dropdown-item pointer" @click="printWithFormulaByTeeth">
          {{ $t('grouped_by_teeth') }}
        </a>
        <a class="dropdown-item pointer" @click="printWithFormulaByServices">
          {{ $t('grouped_by_services') }}
        </a>
        <div class="dropdown-divider"></div>
        <div class="dropdown-header font-weight-bold">{{  $t('by_stages') }}</div>
        <a class="dropdown-item pointer"
           v-for="(complex, index) in object.visits"
           :key="`visit-${complex.id}`"
           @click="printPlanComplex(complex)">
          {{ complex.title ? complex.title : $t('stage')+' '+(index+1) }}
        </a>
      </b-dropdown>
    </a>
    <a v-if="isComplexesView"
       class="data-table-action"
       @click="printPlanComplex(object)"
       :title="$t('print')">
      <PrintSvg/><span class="action-button-mob-title">{{ $t('print') }}</span>
    </a>
    <PlanSignModal v-if="showModal"
                   :plan="planTreatment"
                   @hidden="showModal = false"/>
  </span>
</template>

<script>

import EditSvg from '@/assets/svg-vue/general/edit2.svg'
import PrintSvg from '@/assets/svg-vue/general/print2.svg'
import RefreshSvg from '@/assets/svg-vue/general/refresh2.svg'
import JwtService from "@/services/jwt.service"
import {mapActions, mapState} from "vuex"
import { PatientService } from "@/services/api/patient.api.service"
import {CalendarService} from "@/services/api/calendar.api.service"
import SignSvg from "@/assets/svg-vue/patients/sign.svg.vue"
import PlanSignModal from "@/components/parts/planning/PlanSignModal.vue"
import {copyObject} from "@/extensions/prototypes/prototypes"
import {canEditPlanTreatment} from "@/services/plan.helper"

export default {
  name: "PatientDatatablePlanTreatmentActions",
  components: {
    PlanSignModal,
    SignSvg,
    EditSvg,
    PrintSvg,
    RefreshSvg
  },
  props: {
    object: {
      type: Object
    }
  },
  computed: {
    ...mapState({
      clinic: state   => state.auth.clinic,
      branchId: state => state.auth.branch,
      rights: state => state.auth.rights,
    }),
    isComplexesView() {
      return this.object.model === 'plan_visit'
    },
    planTreatment() {
      return this.isComplexesView ? this.object.plan_treatment : this.object
    },
    planVisit() {
      return this.isComplexesView ? this.object : null
    },
    hasRightToEditPlan() {
      return this.rights.includes('edit_plan_treatment')
          || this.rights.includes('edit_plan_treatment_limited')
    },
    canEditPlan() {
      return canEditPlanTreatment(this.object, this.rights)
    },
    canSign() {
      return this.rights.includes('sign_plan_treatments')
    }
  },
  data() {
    return {
      img: null,
      showModal: false
    }
  },
  methods: {
    ...mapActions(['getPatientTeeth']),
    editPlanTreatment() {
      this.$router.push({
        name: 'TreatmentPlanFullCard',
        params: {
          patientId: this.planTreatment.client_id,
          planId: this.planTreatment.planning_id,
        }
      })
    },
    copyPlanTreatment() {
      CalendarService.duplicatePlanTreatment(this.planTreatment.planning_id).then(() => {
        this.$noty.info(this.$t('success_message'))
        this.$store.commit('incrementDataTableCounter')
      }).catch((e) => { console.log(e) })
    },
    updatePlanTreatmentPrices() {
      CalendarService.updatePlanTreatmentPrices(this.planTreatment.planning_id).then(() => {
        this.$noty.info(this.$t('success_message'))
      }).catch((e) => { console.log(e) })
    },
    printPlan() {
      const link = JwtService.getPrintPrelink(this.clinic)
      window.open(`${link}/plan/${this.planTreatment.planning_id}?branchId=${this.branchId}`, '_blank')
    },
    printPlanComplex(complex) {
      const link = JwtService.getPrintPrelink(this.clinic)
      window.open(`${link}/plan/${this.planTreatment.planning_id}?complexId=${complex.id}&branchId=${this.branchId}`, '_blank')
    },
    printPlanGrouped() {
      const link = JwtService.getPrintPrelink(this.clinic)
      window.open(`${link}/plan/${this.planTreatment.planning_id}?grouped=1&branchId=${this.branchId}`, '_blank')
    },
    printPlanGroupedByTeeth() {
      const link = JwtService.getPrintPrelink(this.clinic)
      window.open(`${link}/plan/${this.planTreatment.planning_id}?grouped=1&by_teeth=1&branchId=${this.branchId}`, '_blank')
    },
    printPlanGroupedByServices() {
      const link = JwtService.getPrintPrelink(this.clinic)
      window.open(`${link}/plan/${this.planTreatment.planning_id}?grouped=1&by_services=1&branchId=${this.branchId}`, '_blank')
    },
    async printWithFormulaByTeeth() {
      await this.printWithFormula('by_teeth')
    },
    async printWithFormulaByServices() {
      await this.printWithFormula('by_services')
    },
    printPlanFact() {
      const link = JwtService.getPrintPrelink(this.clinic)
      window.open(`${link}/plan/${this.planTreatment.planning_id}?planfact=1&branchId=${this.branchId}`, '_blank')
    },
    async printWithFormula(type = 'standard') {
      this.$store.commit('setPatient', copyObject(this.planTreatment.patient))
      await this.getPatientTeeth(this.planTreatment.patient.id)
      this.$store.dispatch('decideWhichPatientFormulaViewToShow', { plan: this.planTreatment })
      await this.$nextTick()
      const el = document.getElementById('patientFormulaSelectedView')
      const options = {
        type: 'dataURL',
        allowTaint: true,
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
        onclone: function(doc) {
          const hiddenDiv = doc.getElementById('patientFormula')
          hiddenDiv.style.display = 'block'
        }
      }
      const style = document.createElement('style') // this style addition is a hack to keep the styling, otherwise some paddings appear and image is cropped
      document.head.appendChild(style)
      style.sheet?.insertRule('body > div:last-child img { display: inline-block; }')
      await this.$nextTick()
      try {
        const output = await this.$html2canvas(el, options)
        style.remove()
        const res = await PatientService.sendFormulaImage(this.planTreatment.client_id, output)
        if (res.data) {
          const link = JwtService.getPrintPrelink(this.clinic)
          let additionalUrl = ''
          if(type === 'by_teeth') additionalUrl = '&grouped=1&by_teeth=1'
          if(type === 'by_services') additionalUrl = '&grouped=1&by_services=1'
          window.open(`${link}/plan/${this.planTreatment.planning_id}?formula=1&branchId=${this.branchId}${additionalUrl}`, '_blank')
        }
      } catch (e) {
        console.log(e)
      }
    },
    printRouteList() {
      const link = JwtService.getPrintPrelink(this.clinic)
      window.open(`${link}/plan/${this.planTreatment.planning_id}?route_list=1&branchId=${this.branchId}`, '_blank')
    },
    async sign() {
      this.showModal = true
      await this.$nextTick()
      this.$bvModal.show('plan-sign-modal')
    },
  }
}
</script>

<style scoped lang="scss">
.inline-action-buttons {
  display: contents;
}
</style>
